import { getPosthog } from "@fideus/analytics";

interface AuthStorageData {
  expiry?: number;
  jsCookies?: boolean;
}

function isAuthStorageData(value: unknown): value is AuthStorageData {
  return (
    typeof value === "object" &&
    value !== null &&
    ("expiry" in value ? typeof value.expiry === "number" : true) &&
    ("jsCookies" in value ? typeof value.jsCookies === "boolean" : true)
  );
}

function captureAuthState() {
  const cookies = document.cookie.split(";").map((c) => c.trim());
  const hasAuthorizationCookie = cookies.some((c) =>
    c.startsWith("authorization=")
  );
  const hasUserCookie = cookies.some((c) => c.startsWith("user="));

  let hasLocalStorageCredentials = false;
  let localStorageCredentialsExpired: boolean | null = null;
  let localStorageJsCookiesFlag: boolean | null = null;
  let credentialsExpiryMs: number | null = null;

  try {
    const raw = localStorage.getItem("AuthenticationCredentialsStorage");
    if (raw) {
      const parsed = JSON.parse(raw) as unknown;
      if (isAuthStorageData(parsed)) {
        hasLocalStorageCredentials = true;
        if (typeof parsed.expiry === "number") {
          credentialsExpiryMs = parsed.expiry - Date.now();
          localStorageCredentialsExpired = parsed.expiry < Date.now();
        }
        if (typeof parsed.jsCookies === "boolean") {
          localStorageJsCookiesFlag = parsed.jsCookies;
        }
      }
    }
  } catch {
    // localStorage unavailable or corrupt — leave defaults
  }

  return {
    hasAuthorizationCookie,
    hasUserCookie,
    hasLocalStorageCredentials,
    localStorageCredentialsExpired,
    localStorageJsCookiesFlag,
    credentialsExpiryMs,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
  };
}

interface LoadTimings {
  configLoadMs: number;
  i18nLoadMs: number;
  providerCreationMs: number;
  totalInitMs: number;
}

/** Snapshot auth cookie/localStorage state before Authress SDK modifies it. */
export function snapshotAuthState() {
  return captureAuthState();
}

/** Send a single diagnostic event with init timings and auth state to PostHog. */
export function reportAppLoadDiagnostics(
  timings: LoadTimings,
  authState: ReturnType<typeof captureAuthState>
) {
  getPosthog()?.capture("AppLoadDiagnostics", {
    ...timings,
    ...authState,
  });
}
